import { useMemo } from "react";
import { buildDiffCircuitPathsState } from "../../utils/buildDiffCircuitPathsState";
import {
  ALL_STATUSES,
  EMPTY_MARKERS,
  EMPTY_RAIL_ENTRIES,
  EMPTY_ROUTES,
  EMPTY_STATUS_COUNTS,
  STATUS_RGB,
} from "../../constants";
import { countItemsByStatus, selectVisibleItems } from "../../utils";
import type { UseVisualDiffOverlay } from "./types";

export const useVisualDiffOverlay: UseVisualDiffOverlay = ({
  visualDiff,
  activeKind,
  visibleStatuses,
  selectedTypeKey,
}) => {
  const visibleMarkers = useMemo(() => {
    if (visualDiff === null || activeKind !== "elements") return EMPTY_MARKERS;
    return selectVisibleItems({
      itemsByStatus: visualDiff.markersByStatus,
      visibleStatuses,
      selectedTypeKey,
    });
  }, [visualDiff, activeKind, visibleStatuses, selectedTypeKey]);

  const visibleRoutes = useMemo(() => {
    if (visualDiff === null || activeKind !== "trays") return EMPTY_ROUTES;
    return selectVisibleItems({
      itemsByStatus: visualDiff.routesByStatus,
      visibleStatuses,
      selectedTypeKey,
    });
  }, [visualDiff, activeKind, visibleStatuses, selectedTypeKey]);

  // NOTE: past ~100k markers, give BatchedRects the status-to-rgb map instead of cloning each marker.
  const detectedObjects = useMemo(
    () =>
      visibleMarkers.map((marker) => ({
        ...marker,
        rgb: STATUS_RGB[marker.status],
      })),
    [visibleMarkers],
  );

  const circuitPaths = useMemo(
    () => buildDiffCircuitPathsState({ diffRoutes: visibleRoutes }),
    [visibleRoutes],
  );

  const countsByStatus = useMemo(() => {
    if (visualDiff === null) return EMPTY_STATUS_COUNTS;
    if (activeKind === "elements") {
      return countItemsByStatus({
        itemsByStatus: visualDiff.markersByStatus,
        selectedTypeKey,
      });
    }
    return countItemsByStatus({
      itemsByStatus: visualDiff.routesByStatus,
      selectedTypeKey,
    });
  }, [visualDiff, activeKind, selectedTypeKey]);

  const railEntries = useMemo(() => {
    if (visualDiff === null) return EMPTY_RAIL_ENTRIES;
    return visualDiff.typeEntries.filter((entry) => entry.kind === activeKind);
  }, [visualDiff, activeKind]);

  const hasItemsInView = ALL_STATUSES.some(
    (status) => countsByStatus[status] > 0,
  );

  const isPlanOverlayEmpty =
    hasItemsInView && detectedObjects.length === 0 && circuitPaths === null;

  return {
    visibleRoutes,
    detectedObjects,
    circuitPaths,
    countsByStatus,
    railEntries,
    isPlanOverlayEmpty,
  };
};
